import { Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CustomLegend } from "./CustomLegend";
import { CHART_COLORS } from "../../constants/ChartColors";
import { convertToPixelFromVH, convertToPixelFromVW } from "../../utils/ViewportUtils";
import { useContext, useMemo, useState } from "react";
import { ViewportContext } from "../../contexts/viewportContext";

const CHAR_PIXEL_SIZE = 2;

export default function BarChartContainer({ visual, enalrged = false }: { visual: any, enalrged?: boolean }) {

  const vpContext = useContext(ViewportContext);

  const largeLabelLength = (Math.max(...visual.x_series.map((xl: any) => xl.length)));
  const adjustHeight = (largeLabelLength * CHAR_PIXEL_SIZE);

  const [maxLengthForYLabel, setMaxLengthForYLabel] = useState(0);

  const adjustYLabelWidth = useMemo(() => {
    if (vpContext?.is4k)
      return maxLengthForYLabel * 1.25;
    if (vpContext?.is2k)
      return maxLengthForYLabel * 0.75;
    return maxLengthForYLabel * 0.25
  }, 
  [maxLengthForYLabel, vpContext?.is2k, vpContext?.is4k]);

  const height = enalrged ? 35 + adjustHeight / 2 : 20 + adjustHeight / 3;
  const margins = { left: convertToPixelFromVW(adjustYLabelWidth), bottom: convertToPixelFromVH(adjustHeight / 3) };

  const dataKey = Object.keys(visual.x_label)[0];
  const xAxisLabel = visual.x_label[dataKey];

  const data = () => {
    const converted_data = visual.x_series.map((s: any, index: number) => {
      return {
        [dataKey]: s,
        ...getDataRow(index)
      }
    })
    return converted_data;
  };

  const getDataRow = (index: number) => {
    const d = Object.keys(visual.y_label).map((s) => {
      const value = visual.y_series[s as keyof typeof visual.y_series][index];
      if (value.toString().length > maxLengthForYLabel) setMaxLengthForYLabel(value.toString().length)
      return {
        [s]: value
      }
    })
    return d.reduce((acc, obj) => {
      return { ...acc, ...obj };
    }, {});
  }

  return (
    <>
      {
        !enalrged && <Typography variant='h6' textAlign={'center'}>
          {visual.title}
        </Typography>
      }
      <ResponsiveContainer width="100%" height={convertToPixelFromVH(height)}>
        <BarChart data={data()} margin={margins}>
          {/* <defs>
            {
              CHART_COLORS.map((color, index) => {
                return <linearGradient id={`bar_gradient_${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={`${color}AA`} />
                  <stop offset="95%" stopColor={color} />
                </linearGradient>
              })
            }
          </defs> */}
          <CartesianGrid strokeDasharray="2 2" strokeWidth={0.75} vertical={false} />
          <XAxis dataKey={dataKey} angle={-45} textAnchor="end" tick={{ color: '#000000', fontWeight: 700 }}>
            <Label value={xAxisLabel} position={'bottom'} offset={convertToPixelFromVW(largeLabelLength) * 0.25} />
          </XAxis>
          <YAxis tick={{ color: '#000000', fontWeight: 700 }} />
          <Tooltip labelStyle={{ color: '#000000' }} />
          <Legend verticalAlign="top"
            margin={{ bottom: 10 }}
            iconType="circle"
            align="center"
            layout="vertical"
            content={<CustomLegend />} />
          {
            Object.keys(visual.y_label).map((s, index) => {
              return <Bar
                key={`bar-${index}`}
                type="monotone"
                dataKey={s}
                fill={`${CHART_COLORS[index]}`}
                maxBarSize={vpContext?.is4k ? 150 : 50} />
            })
          }
        </BarChart>
      </ResponsiveContainer>
    </>)
}